import React from 'react';
import { Remarkable } from 'remarkable';
import DOMPurify from 'dompurify';
import regexMap from '../constants/regex-map';
import { checkRequiredFields } from './cms-required-fields';

const bodyTextCssClasses = {
  GALLERY_CSS_CLASS: 'Markdown-gallery',
  TABLE_CSS_CLASS: 'Markdown-table',
  TABLE_3_COLUMN_CSS_CLASS: 'Markdown-3ColumnTable',
  TABLE_4_COLUMN_CSS_CLASS: 'Markdown-4ColumnTable',
};

const themeColors = {
  COLOR_WHITE: 'white',
};

function generateJumplinks(components) {
  const jumpLinks = [];
  let headerIndex = false;

  components.forEach((component, i) => {
    const title = component.jumpLinkText || component.title;
    if (component.isInJumpLinks && title) {
      jumpLinks.push({
        text: title,
        id: `CMSComponent-${i}`,
      });
    }

    if (headerIndex === false && component.component === 'content-header') {
      headerIndex = i;
    }
  });

  if (jumpLinks.length && headerIndex !== false) {
    components[headerIndex].jumpLinks = jumpLinks;
  }

  return components;
}

function simpleMarkdown(text) {
  const md = new Remarkable();
  // Hack to render markdown right as drupal strips the line breaks
  const parsedMarkdown = md.render(text.replace(/\n/g, '\n\n'));
  const sanitize = DOMPurify.sanitize;

  return (
    <span dangerouslySetInnerHTML={{ __html: sanitize(parsedMarkdown) }} />
  );
}

function bodyTextMarkdown(text) {
  if (!text) {
    return '';
  }

  const components = [
    'BUTTON',
    'CHECKLIST',
    'TEXTLIST',
    'PULLQUOTE',
    'CALLOUT',
    'CALLOUT\\-RIGHT',
    'IMAGE',
    'IMAGEINSET',
    'IMAGEINSET\\-RIGHT',
    'LOCATIONS',
    'LOCATIONS\\-RIGHT',
    'LOCATIONS\\-ITEM',
    'CALLOUT\\-2UP',
    'CALLOUT\\-3UP',
    'LIST\\-OF\\-ITEMS',
    'LIST\\-OF\\-ITEMS\\-WITH\\-PRICE',
    'LIST\\-ITEM',
  ].join('|');

  const md = new Remarkable({ html: true });
  const openTags = text.match(new RegExp(`\\[(${components})\\]`, 'g'));

  if (openTags && openTags.length) {
    for (let i = 0; i < openTags.length; i++) {
      const openTag = openTags[i];
      const closeTag =  openTag[0] + '/' + openTag.substr(1);
      const cssClass = openTag.substr(1, openTag.length - 2).toLowerCase();

      const openTagPosition = text.indexOf(openTag);
      const closeTagPosition = text.indexOf(closeTag);

      // Check if there is matching close tag after open tag
      if (closeTagPosition >= 0 && closeTagPosition > openTagPosition) {
        text = text
          .replace(openTag, `<div class="Markdown-${cssClass}">`)
          .replace(closeTag, '</div>');
      }
    }
  }

  text = text
    // "External" components for the article page
    .replace(/\[GALLERY\]/g, `<div class="${bodyTextCssClasses.GALLERY_CSS_CLASS}"></div>`)
    .replace(/\[TABLE\]/g, `<div class="${bodyTextCssClasses.TABLE_CSS_CLASS}"></div>`)
    .replace(/\[TABLE\-3\-COLUMN\]/g, `<div class="${bodyTextCssClasses.TABLE_3_COLUMN_CSS_CLASS}"></div>`)
    .replace(/\[TABLE\-4\-COLUMN\]/g, `<div class="${bodyTextCssClasses.TABLE_4_COLUMN_CSS_CLASS}"></div>`)
    // Cleans up any non-matched tags
    .replace(new RegExp(`\\[/?(${components})\\]`, 'g'), '')
    // Hack to render markdown right as drupal strips the line breaks
    .replace(/\n/g, '\n\n');

  return md.render(text);
}

function parseMultiField(data, parse, separator) {
  separator = separator || '|||';
  parse = parse || false;

  if (!data) {
    return [];
  }

  data = data.split(separator);

  for (let i = 0; i < data.length; i++) {
    data[i] = data[i].replace(regexMap.NEW_LINES, '').trim();

    if (parse) {
      try {
        data[i] = JSON.parse(data[i]);
      } catch (error) {
        data[i] = null;
      }
    }
  }

  return data;
}

function _parseCmsFields(data) {
  const booleanFields = [
    'addTopPadding',
    'bottomBorder',
    'centerAligned',
    'chatButton',
    'check',
    'icons',
    'isInJumpLinks',
    'legalType',
    'open',
    'roundtrip',
    'showOriginDealsInfo',
    'showSocialMediaIcons',
    'textInsideCircle',
    'titleDisplay',
    'twoCol',
  ];

  const jsonFields = [
    'advertisementsList',
    'advertisments',
    'airports',
    'articles',
    'body',
    'breadcrumbs',
    'destinations',
    'exploreMoreArticles',
    'head',
    'items',
    'media',
    'mediaGallery',
    'moreDestinations',
    'options',
    'originCities',
    'superOptions',
    'table',
    'threeColumnFilteredTable',
    'fourColumnFilteredTable',
  ];

  booleanFields.forEach((booleanField) => {
    if (data[booleanField]) {
      data[booleanField] = data[booleanField] === 'true' || data[booleanField] === '1';
    }
  });

  jsonFields.forEach((jsonField) => {
    if (data[jsonField]) {
      data[jsonField] = JSON.parse(data[jsonField]);
    }
  });
}

function parseCmsData(data) {
  const componentsArray = [];
  const isArray = Array.isArray(data);

  // Array of pages components
  if (isArray && data.length) {
    data.forEach((componentData) => {
      _parseCmsFields(componentData);

      if (checkRequiredFields(componentData)) {
        componentsArray.push(componentData);
      }
    });
  } else if (data) {
    _parseCmsFields(data);
  }

  return isArray ? componentsArray : data;
}

/* returning false or true depending on contrast,
   so it will decide should text be white or colored */
function getColorContrast(color) {
  let hexColor = '';
  if (color.length === 4 && color[0] === '#') {
    hexColor =
      `#${color[1]}${color[1]}` +
      `${color[2]}${color[2]}` +
      `${color[3]}${color[3]}`;
  } else {
    hexColor = color;
  }

  const isColor = hexColor.match(regexMap.HEX_COLOR_CODE) !== null;

  if (isColor) {
    const r = parseInt(hexColor.substr(1, 2), 16);
    const g = parseInt(hexColor.substr(3, 2), 16);
    const b = parseInt(hexColor.substr(5, 2), 16);
    const contrast = ((r * 299) + (g * 587) + (b * 114)) / 1000;
    return (contrast < 128) ? true : false ;
  } else {
    return false;
  }
}

function prepareSchemaTags(schemaTags) {
  /*
    SchemaTags are coming from the CMS in the folowing format:

    title|Aeromexico - Passengers
    meta|property==og:title|content==Aeromexico
    meta|property==og:site_name|content==Aeromexico
    meta|property==og:url|content==https://aeromexico.com
    link|rel==canonical|href==https://aeromexico.com

    title is example of the tag with content
    meta and links are are self closing, and have attributes

    title|Aeromexico - Passengers
    renders to:
    <title>Aeromexico - Passengers</title>

    meta|property==og:title|content==Aeromexico
    renders to:
    <meta property='og:title' content='Aeromexico' />
   */

  schemaTags = schemaTags.replace(regexMap.CARRIAGE_RETURN, '').split('\n');
  const tagsObject = {};

  schemaTags.forEach((line) => {
    line = line.trim();

    if (line === '') {
      return;
    }

    const parts = line.split('|');
    const tag = parts[0];
    let i = 0;

    if (tag === 'title') {
      if (parts.length === 2) {
        tagsObject[tag] = parts[1];
      }
    } else {
      // meta and rest
      const attrObject = {};

      for (i = 1; i < parts.length; i++) {
        const attr = parts[i].split('==');
        if (attr.length === 2) {
          attrObject[attr[0]] = attr[1];
        }
      }

      if (Object.keys(attrObject).length) {
        if (!tagsObject[tag]) {
          tagsObject[tag] = [];
        }

        tagsObject[tag].push(attrObject);
      }
    }
  });

  return tagsObject;
}

export {
  bodyTextCssClasses,
  bodyTextMarkdown,
  generateJumplinks,
  getColorContrast,
  parseCmsData,
  parseMultiField,
  simpleMarkdown,
  themeColors,
  prepareSchemaTags,
};
